import { restoreErrorMessage, useRestoreWorkspace } from "@/api/archived";
import { useWorkspaceById } from "@/api/workspaces";
import { InTrashBanner } from "@/components/InTrashBanner/InTrashBanner";
import { withMessageInputContext } from "@/context/MessageInputContext";
import { withSessionProvider } from "@/context/SessionContext";
import { WorkspaceDropTarget } from "@/dnd/WorkspaceDropTarget";
import { UploadingCard } from "@cohesive/ui-components";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";
import { WorkspaceDropZone } from "./components/WorkspaceDropZone";
import { HomeSurface } from "./home/HomeSurface";
import { WorkspaceSurfaceHeader } from "./home/WorkspaceSurfaceHeader";
import { useWorkspaceFolderNavigation } from "./hooks/useWorkspaceFolderNavigation";
import { useWorkspaceUploadQueue } from "./hooks/useWorkspaceUploadQueue";

/**
 * Workspace page: the home surface, force-filtered to one workspace. The
 * workspace name replaces the greeting, a scope chip rides inside the composer
 * frame, and sending starts a chat in this workspace.
 *
 * Drops split by target: the composer nests its own zone (attach to message),
 * and this page's zone catches everything else (upload into the workspace).
 * `WorkspaceLayout` already disables its own `OsFileDropLayer` overlay on this
 * route, so the two never fight.
 */
function WorkspaceDetailBody({ workspaceId }: { workspaceId: string }) {
  const { data: workspace } = useWorkspaceById(workspaceId);

  const restoreWorkspace = useRestoreWorkspace({
    onSuccess: () => toast.success("Restored Workspace."),
    onError: (err) =>
      toast.error(restoreErrorMessage(err, "Failed to restore Workspace")),
  });

  const {
    queue,
    enqueueFiles,
    enqueueTree,
    cancelUpload,
    suppressedRecordIds,
  } = useWorkspaceUploadQueue(workspaceId);

  // Page-level drops land in the folder the user is viewing (the browser's
  // Files-tab `?path` drill-in); at the root (or on other tabs) that's "".
  const { currentPath } = useWorkspaceFolderNavigation();

  const workspaceName = workspace?.is_private
    ? "My workspace"
    : (workspace?.name ?? "Workspace");

  // Only ghost-tile uploads whose destination matches the viewed path —
  // dropping an OS folder tree otherwise briefly renders ghosts for every
  // leaf file at the root before they slot into their real subfolders.
  const visibleQueue = queue.filter(
    (item) => (item.destinationParentPath ?? "") === currentPath,
  );

  return (
    <WorkspaceDropZone
      className="h-full"
      onDrop={({ files, tree }) =>
        tree ? enqueueTree(tree, currentPath) : enqueueFiles(files, currentPath)
      }
    >
      <title>{workspaceName}</title>
      {workspace?.archived_at && (
        <InTrashBanner
          kind="workspace"
          isRestoring={restoreWorkspace.isPending}
          onRestore={() => restoreWorkspace.mutate(workspaceId)}
          className="mb-6"
        />
      )}
      {/* In-app root drop target (the OS zone above only handles native file
          drops): rail/canvas drags released anywhere on this page land in the
          folder being viewed — the old detail page's whole-surface target,
          which the classifier's workspace-root rules assume exists. Smaller
          targets inside (folder cards, crumbs, list rows) still win via
          deepest-droppable collision. */}
      <WorkspaceDropTarget
        workspaceId={workspaceId}
        parentPath={currentPath}
        className="flex h-full min-h-0 flex-col"
      >
        <HomeSurface
          workspaceId={workspaceId}
          workspaceName={workspaceName}
          workspaceIcon={workspace?.metadata?.icon}
          title={
            <WorkspaceSurfaceHeader
              workspace={workspace}
              workspaceName={workspaceName}
            />
          }
          browserLeadingSlot={
            visibleQueue.length > 0 ? (
              <>
                {visibleQueue.map((item) => (
                  <UploadingCard
                    key={item.tempId}
                    entry={{
                      id: item.tempId,
                      name: item.name,
                      status: item.status,
                      error: item.error,
                      // Only a failed local upload is the user's to clear.
                      dismissable: item.status === "error",
                    }}
                    onDismiss={() => cancelUpload(item.tempId)}
                  />
                ))}
              </>
            ) : undefined
          }
          suppressedRecordIds={suppressedRecordIds}
        />
      </WorkspaceDropTarget>
    </WorkspaceDropZone>
  );
}

const ScopedWorkspaceSurface = withMessageInputContext(
  withSessionProvider(WorkspaceDetailBody),
);

export function WorkspaceDetailPage() {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  if (!workspaceId) return null;

  // React Router reuses this element when only `:workspaceId` changes, so
  // `SessionProvider` would otherwise outlive the switch and carry workspace
  // A's auto-created flow session (created on first file attach) into
  // workspace B. The key must sit ABOVE the providers to reset it.
  return <ScopedWorkspaceSurface key={workspaceId} workspaceId={workspaceId} />;
}

export default WorkspaceDetailPage;
